import axiosClient from "axios-client.js";
import { fetchData } from "utils/fetchData";
import { getMonthName } from "utils/months";
import { useNavigate } from "react-router-dom";
import { createContext, useContext, useState, useEffect } from "react";
import {
    initialLicenses,
    initialServerActivations,
} from "utils/homepageInitialData";

const StateContext = createContext({
    currentUser: null,
    token: null,
    searchValue: null,
    setUser: () => {},
    setToken: () => {},
    setSearchValue: () => {},
    checkToken: () => {},
    analyticsLastUpdateDate: "",
    licenses: [],
    licensesList: [],
    serverActivations: [],
    serverActivationsByMonth: [],
    getLicenses: () => {},
    getActivations: () => {},
    clearAnalyticsData: () => {},
});

export const getCookie = (name) => {
    const cookies = document.cookie.split(";");

    for (var i = 0; i < cookies.length; i++) {
        const cookiePair = cookies[i].split("=");
        const cookieName = cookiePair[0];
        const cookieValue = decodeURIComponent(cookiePair[1]);

        if (name.trim() === cookieName.trim()) {
            return cookieValue;
        }
    }

    return localStorage.getItem(name);
};

let refreshInterval;

export const ContextProvider = ({ children }) => {
    const navigate = useNavigate();

    const [user, setUser] = useState(() => {
        const storedUser = localStorage.getItem("user");
        return storedUser ? JSON.parse(storedUser) : null;
    });
    const [searchValue, setSearchValue] = useState("");
    const [token, _setToken] = useState(getCookie("ACCESS_TOKEN"));

    const [analyticsLastUpdateDate, setAnalyticsLastUpdateDate] = useState("");
    const [licenses, setLicenses] = useState(initialLicenses);
    const [licensesList, setLicensesList] = useState();
    const [serverActivations, setServerActivations] = useState(
        initialServerActivations
    );
    const [serverActivationsByMonth, setServerActivationsByMonth] = useState(
        []
    );

    useEffect(() => {
        if (serverActivations.length === 0) return;

        const existing = localStorage.getItem("analytics-activations");
        const lastUpdated = existing
            ? JSON.parse(existing).lastUpdated
            : Date.now();

        localStorage.setItem(
            "analytics-activations",
            JSON.stringify({
                lastUpdated,
                payload: serverActivations,
            })
        );
    }, [serverActivations]);

    useEffect(() => {
        if (licenses.length === 0) return;

        const existing = localStorage.getItem("analytics-licenses");
        const lastUpdated = existing
            ? JSON.parse(existing).lastUpdated
            : Date.now();

        localStorage.setItem(
            "analytics-licenses",
            JSON.stringify({
                lastUpdated,
                payload: licenses,
            })
        );
    }, [licenses]);

    const navigateToPendingActivations = () => {
        navigate("/my/pending-activations", {
            replace: true,
        });
    };

    const hasDataExpired = (lastUpdated) => {
        const diffInMs = Date.now() - lastUpdated;
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

        return diffInHours > 4;
    };

    const setLicensesValueByType = (type, value) => {
        if (type !== "expired") {
            setLicenses((licenses) =>
                licenses.map((license) => {
                    if (license.id === type) {
                        return { ...license, value };
                    }

                    return license;
                })
            );
        } else {
            setLicenses((licenses) =>
                licenses.map((license) => {
                    if (type === "expired" && license.id === type) {
                        return {
                            ...license,
                            value,
                            color: value > 0 ? "brand" : "primary",
                        };
                    }

                    return license;
                })
            );
        }
    };

    useEffect(() => {
        localStorage.setItem("user", JSON.stringify(user));
    }, [user]);

    useEffect(() => {
        const intervalTime = 20 * 60 * 1000; // 30 minutes

        refreshInterval = setInterval(() => {
            checkToken();
        }, intervalTime);

        return () => clearInterval(refreshInterval);
    }, [token]);

    const setToken = (token) => {
        _setToken(token);
        if (token) {
            document.cookie =
                "ACCESS_TOKEN=" +
                token +
                "; SameSite=strict; Max-Age=3600; path=/";
        } else {
            localStorage.removeItem("user");
            localStorage.removeItem("ACCESS_TOKEN");
            document.cookie =
                "ACCESS_TOKEN=; SameSite=strict; Max-Age=0; path=/";
            clearInterval(refreshInterval);
        }
    };

    const checkToken = () => {
        const token = getCookie("ACCESS_TOKEN");
        if (token) {
            axiosClient
                .post("/auth/refresh", {})
                .then(({ data }) => {
                    setToken(data.token);
                    setUser(data.user_data);
                })
                .catch((err) => {
                    console.log("refresh error: " + err.response);
                    setToken(null);
                    setUser(null);
                    navigate("/auth/sign-in", {
                        replace: true,
                    });
                });
        } else {
            setToken(null);
        }
    };

    const getActivations = async () => {
        const cachedActivations = localStorage.getItem("analytics-activations");
        const cachedActivationsByMonth = localStorage.getItem(
            "analytics-activations-by-month"
        );

        if (cachedActivations && cachedActivationsByMonth) {
            const { lastUpdated, payload: activations } =
                JSON.parse(cachedActivations);
            const { payload: activationsByMonth } = JSON.parse(
                cachedActivationsByMonth
            );

            if (hasDataExpired(lastUpdated)) {
                await fetchActivationsPendingCount();
                await fetchActivationsByMonth();

                setAnalyticsLastUpdateDate(new Date(Date.now()));
            } else {
                setServerActivations(
                    activations.map((activation) =>
                        activation.id === "pending"
                            ? {
                                  ...activation,
                                  onClick:
                                      activation.value > 0
                                          ? navigateToPendingActivations
                                          : null,
                              }
                            : activation
                    )
                );

                setServerActivationsByMonth(activationsByMonth);
                setAnalyticsLastUpdateDate(new Date(lastUpdated));
            }
        } else {
            await fetchActivationsPendingCount();
            await fetchActivationsByMonth();

            setAnalyticsLastUpdateDate(new Date(Date.now()));
        }
    };

    const fetchActivationsByMonth = async () => {
        const res = await fetchData("analytics/activations/timeseries");

        const mappedRes = Object.entries(res).map(([date, data]) => ({
            label: getMonthName(date.split("-")[1]),
            value: Object.values(data).reduce((acc, cur) => acc + cur, 0),
            ...data,
        }));

        const lastSixMonthsTotal = mappedRes
            .slice(6)
            .map(({ value }) => value)
            .reduce((acc, curr) => acc + curr, 0);

        const lastYearTotal = mappedRes
            .map(({ value }) => value)
            .reduce((acc, curr) => acc + curr, 0);

        setServerActivationsByMonth(mappedRes);
        setServerActivations((activations) =>
            activations.map((activation) => {
                if (activation.id === "lastSixMonths") {
                    return { ...activation, value: lastSixMonthsTotal };
                }

                if (activation.id === "lastYear") {
                    return { ...activation, value: lastYearTotal };
                }

                return activation;
            })
        );

        const cacheObjectActivationsByMonth = {
            lastUpdated: new Date().getTime(),
            payload: mappedRes,
        };
        localStorage.setItem(
            "analytics-activations-by-month",
            JSON.stringify(cacheObjectActivationsByMonth)
        );
    };

    const fetchActivationsPendingCount = async () => {
        const res = await fetchData("analytics/activations/pending/count");

        setServerActivations((activations) =>
            activations.map((activation) =>
                activation.id === "pending"
                    ? {
                          ...activation,
                          value: res,
                          color: res > 0 ? "warning" : "primary",
                          onClick:
                              res > 0 ? navigateToPendingActivations : null,
                      }
                    : activation
            )
        );
    };

    const getLicenses = async (filter = null, filterMode) => {
        if (filter !== null && filterMode) {
            await fetchLicensesList(filter, true);
            return;
        }

        const cachedLicensesCount = localStorage.getItem("analytics-licenses");
        const cachedLicensesList = localStorage.getItem(
            "analytics-licenses-list"
        );

        if (cachedLicensesCount && cachedLicensesList) {
            const { lastUpdated, payload: licenses } =
                JSON.parse(cachedLicensesCount);
            const { payload: licensesList } = JSON.parse(cachedLicensesList);

            if (hasDataExpired(lastUpdated)) {
                const expiredLicenses = await fetchLicensesCount(0);
                await fetchLicensesCount(3, expiredLicenses);
                await fetchLicensesCount(6, expiredLicenses);
                await fetchLicensesList(0);

                setAnalyticsLastUpdateDate(new Date(Date.now()));
            } else {
                setLicenses(licenses);

                setLicensesList(licensesList);
                setAnalyticsLastUpdateDate(new Date(lastUpdated));
            }
        } else {
            const expiredLicenses = await fetchLicensesCount(0);
            await fetchLicensesCount(3, expiredLicenses);
            await fetchLicensesCount(6, expiredLicenses);
            await fetchLicensesList(0);

            setAnalyticsLastUpdateDate(new Date(Date.now()));
        }
    };

    const fetchLicensesCount = async (months = 0, expiredLicenses = 0) => {
        if (months !== 0 && months !== 3 && months !== 6) return;

        const res = await fetchData(`analytics/expiring/${months}/count`);

        const total = Object.values(res).reduce((acc, curr) => acc + curr, 0);
        const adjustedTotal = total - expiredLicenses;

        if (months === 0) {
            setLicensesValueByType("expired", total);
        }
        if (months === 3) {
            setLicensesValueByType("expiringThreeMonths", adjustedTotal);
        }
        if (months === 6) {
            setLicensesValueByType("expiringSixMonths", adjustedTotal);
        }

        return total;
    };

    const fetchLicensesList = async (months = 0, skipLocalStorage = false) => {
        const res = await fetchData(`analytics/expiring/${months}/list`);

        const mappedRes = Object.entries(res).flatMap(([type, licenses]) =>
            licenses.map((license) => ({ ...license, type }))
        );

        const filteredRes =
            months > 0
                ? mappedRes.filter(
                      (license) => new Date(license.expires_at) > Date.now()
                  )
                : mappedRes;

        const sortedRes = filteredRes.sort((a, b) => {
            return months === 0
                ? new Date(b.expires_at) - new Date(a.expires_at)
                : new Date(a.expires_at) - new Date(b.expires_at);
        });

        setLicensesList(sortedRes);

        if (skipLocalStorage) return;

        const cacheObjectLicensesList = {
            lastUpdated: new Date().getTime(),
            payload: sortedRes,
        };

        localStorage.setItem(
            "analytics-licenses-list",
            JSON.stringify(cacheObjectLicensesList)
        );
    };

    const clearAnalyticsData = () => {
        localStorage.setItem("analytics-licenses", "");
        localStorage.setItem("analytics-licenses-list", "");

        localStorage.setItem("analytics-activations", "");
        localStorage.setItem("analytics-activations-by-month", "");
    };

    return (
        <StateContext.Provider
            value={{
                user,
                setUser,
                token,
                setToken,
                searchValue,
                setSearchValue,
                checkToken,
                analyticsLastUpdateDate,
                licenses,
                licensesList,
                serverActivations,
                serverActivationsByMonth,
                getLicenses,
                getActivations,
                clearAnalyticsData,
            }}
        >
            {children}
        </StateContext.Provider>
    );
};

export const useStateContext = () => useContext(StateContext);
